/**
 * Single source of truth for public-route head metadata.
 *
 * Consumed by:
 *  - src/components/SeoHead.tsx  (runtime <Helmet> tags)
 *  - scripts/bakeRouteHtml.ts    (build-time dist/<route>/index.html)
 *
 * Keep this file dependency-free: it is imported by the Vite build config.
 */

export const SITE_URL = 'https://www.ledlogic.tech';
export const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

export interface RouteMeta {
  /** Full <title> text. */
  title: string;
  /** meta description (also og:description / twitter:description unless overridden). */
  description: string;
  /** og:title / twitter:title when it should differ from the page title. */
  ogTitle?: string;
  /** og:description when it should differ from the meta description. */
  ogDescription?: string;
}

export const ROUTE_META: Record<string, RouteMeta> = {
  '/': {
    title: 'LED Logic — Pro LED Panel Configurator',
    description:
      'Design and configure LED screen installations with precise power, data, and rigging calculations.',
  },
  '/pricing': {
    title: 'Pricing & Plans — LED Logic',
    description:
      'LED Logic pricing — Free, Panel Basic, Flow Pro, Signal Premium, and Enterprise tiers for AV pros, integrators, and rental companies.',
    ogDescription:
      'Free, Panel Basic, Flow Pro, Signal Premium, and Enterprise tiers for AV pros, integrators, and rental companies.',
  },
  '/compare': {
    title: 'Compare Panels — LED Logic',
    description:
      'Compare LED panels side-by-side — pixel pitch, resolution, processors, and power. No sign-in required.',
    ogDescription: 'Compare LED panels side-by-side — no sign-in required.',
  },
  '/pitch-compare': {
    title: 'Pitch Compare — LED Logic',
    description:
      'Visualise how pixel pitches look at any viewing distance. No sign-in required.',
    ogDescription: 'Visualise how pixel pitches look at any viewing distance.',
  },
  '/troubleshooting': {
    title: 'Free LED Wall Troubleshooting — LED Logic',
    description:
      'Free, no-sign-in troubleshooting for LED video walls. Pick a symptom — flicker, dead area, color, or no signal — and get a ranked list of fixes to try.',
  },
  '/thermal': {
    title: 'LED Wall Thermal & HVAC Calculator — LED Logic',
    description:
      'Calculate heat load, BTU/hr, and HVAC tonnage for LED video walls based on panel power draw, efficiency, and ambient overhead.',
  },
  '/help': {
    title: 'Help & Getting Started — LED Logic',
    description:
      'Five-step guide to configuring LED video walls — choose a brand, set layout, route data and power, and export a professional report.',
  },
  '/support': {
    title: 'Support — LED Logic',
    description:
      'Get help with LED Logic. Email support or send a message and our team will respond within 2 business days.',
  },
  '/changelog': {
    title: "What's new — LED Logic",
    description:
      'Release notes for LED Logic — new features and improvements in the LED wall configurator, in plain language.',
  },
  '/reference': {
    title: 'Technical Reference — LED Logic',
    description:
      'NovaStar, Brompton, and COEX capacity tables, processor specs, and compatibility matrices used by the LED Logic configurator.',
  },
  '/privacy': {
    title: 'Privacy Policy — LED Logic',
    description:
      "What data LED Logic collects, how it's used, GDPR/CCPA rights, and retention windows for configurator users.",
  },
  '/terms': {
    title: 'Terms of Service — LED Logic',
    description:
      'Terms of use for LED Logic during beta, including liability disclaimers and EU/UK/CCPA regional addendums.',
  },
  '/accessibility': {
    title: 'Accessibility Statement — LED Logic',
    description:
      "LED Logic's accessibility commitments, WCAG conformance level, supported assistive technologies, and how to report barriers.",
  },
  '/attribution': {
    title: 'Attribution & Credits — LED Logic',
    description:
      'Open-source libraries, fonts, and manufacturer trademarks used by LED Logic, with license information.',
  },
  '/dpia': {
    title: 'Data Protection Impact Assessment — LED Logic',
    description:
      'GDPR Data Protection Impact Assessment for the LED Logic Panel Recommender — data inputs, local processing, retention and mitigations.',
  },
  '/configurator/custom': {
    title: 'Custom Panel Builder — LED Logic',
    description:
      'Enter your own LED panel specs — pitch, cabinet size, power and weight — and configure a full wall around them.',
  },
};

/** Routes baked to static HTML at build time (the sitemap's indexable routes). */
export const BAKED_ROUTES: string[] = [
  '/',
  '/pricing',
  '/compare',
  '/pitch-compare',
  '/troubleshooting',
  '/thermal',
  '/help',
  '/support',
  '/changelog',
  '/reference',
  '/privacy',
  '/terms',
  '/accessibility',
  '/attribution',
  '/dpia',
];

/** Canonical URL for a route path; the homepage keeps its trailing slash. */
export function canonicalFor(path: string): string {
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

export function metaFor(path: string): RouteMeta | undefined {
  return ROUTE_META[path];
}
